import { createElement, lazy, useState } from 'react'
import type { ComponentProps, ComponentType, FunctionComponent } from 'react'
import { isChunkLoadError, recoverFromChunkFailure } from './chunkRecovery'

// React's own lazy() is typed `lazy<T extends ComponentType<any>>`, so this
// constraint has to match it exactly; ComponentType<never> and
// ComponentType<unknown> both fail to satisfy React.lazy. Scoped disable
// rather than a repo-wide exception.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface PreloadableComponent<T extends ComponentType<any>>
  extends FunctionComponent<ComponentProps<T>> {
  /** Fetch and evaluate the chunk without rendering. Idempotent. */
  preload: () => Promise<void>
}

/**
 * Wraps React.lazy with two additions.
 *
 * 1. Cache-purging recovery for stale chunk hashes after a deploy.
 *    window.location.reload() is served through the service worker, so when
 *    Cache Storage held a bad body under the chunk's URL a plain reload
 *    returned the identical failure. On a recognised chunk-load error we purge
 *    Cache Storage, refresh the service worker registration, and only then
 *    reload. The attempt counter is time-boxed and shared across all lazy
 *    routes. See chunkRecovery.ts and public/sw.js.
 *
 * 2. A preload() hook, and a direct render path once the chunk is loaded.
 *    Public routes are prerendered to static HTML at build time. main.tsx
 *    preloads the current route's chunk BEFORE the first React render so that
 *    render commits the whole page in one go: the prerendered content is
 *    replaced by an identical tree instead of a Suspense spinner, which is what
 *    was costing ~1.6 s of LCP render delay and most of the CLS (Sep 2026).
 *    The direct-vs-lazy choice is made once per mounted instance so a route
 *    that started on the lazy path never remounts (and loses state) when the
 *    chunk lands.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): PreloadableComponent<T> {
  let resolved: T | null = null

  const load = async (): Promise<{ default: T }> => {
    const mod = await factory()
    resolved = mod.default
    return mod
  }

  const Lazy = lazy(async () => {
    try {
      // Deliberately does NOT reset the recovery counter. Clearing it on any
      // successful load meant a working route could refill the budget for a
      // broken one, so a user bouncing between them could reload forever. The
      // counter ages out on its own after ATTEMPT_WINDOW_MS instead, which
      // caps recovery reloads per window across the whole app.
      return await load()
    } catch (error) {
      if (!isChunkLoadError(error)) {
        throw error
      }

      const recovering = await recoverFromChunkFailure(error)
      if (!recovering) {
        // Budget spent. Let the error boundary render; its Try Again button
        // runs the same recovery rather than a bare reload.
        throw error
      }

      // A reload is in flight. Never resolve, so React does not render a
      // half-torn tree in the interim.
      return new Promise<{ default: T }>(() => {})
    }
  })

  const Preloadable = ((props: ComponentProps<T>) => {
    const [direct] = useState<T | null>(() => resolved)
    if (direct) {
      return createElement(direct as ComponentType<ComponentProps<T>>, props)
    }
    return createElement(Lazy as ComponentType<ComponentProps<T>>, props)
  }) as PreloadableComponent<T>

  Preloadable.preload = async () => {
    await load()
  }

  return Preloadable
}
